import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Smartphone, 
  Monitor, 
  Check, 
  X, 
  Clock, 
  Shield, 
  ShieldCheck, 
  ShieldX,
  Trash2,
  RefreshCw,
  Search,
  AlertTriangle,
  User,
  Globe,
  Calendar
} from 'lucide-react';

const DeviceApproval = () => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [allRequests, setAllRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDevices, setUserDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDenyDialog, setShowDenyDialog] = useState(false);
  const [showUserDevicesDialog, setShowUserDevicesDialog] = useState(false);
  const [denyReason, setDenyReason] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchEmail, setSearchEmail] = useState('');
  const [activeTab, setActiveTab] = useState('pending');

  // Fetch pending device requests
  const fetchPendingRequests = useCallback(async () => {
    try {
      const response = await axios.get('/admin/device-requests?status=pending', {
        withCredentials: true
      });
      setPendingRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch pending requests:', error);
    }
  }, []);

  // Fetch all device requests
  const fetchAllRequests = useCallback(async () => {
    try {
      const response = await axios.get('/admin/device-requests?status=all', {
        withCredentials: true
      });
      setAllRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to fetch all requests:', error);
    }
  }, []);

  // Fetch users with device approval info
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get('/admin/users', {
        withCredentials: true
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchPendingRequests(),
        fetchAllRequests(),
        fetchUsers()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchPendingRequests, fetchAllRequests, fetchUsers]);

  // Auto-refresh pending requests every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchPendingRequests, 30000);
    return () => clearInterval(interval);
  }, [fetchPendingRequests]);

  // Approve device request
  const handleApprove = async (requestId) => {
    try {
      await axios.post(`/admin/device-requests/${requestId}/approve`, {}, {
        withCredentials: true
      });
      toast.success('Device approved successfully');
      fetchPendingRequests();
      fetchAllRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve device');
    }
  };

  // Open deny dialog
  const handleDenyClick = (request) => {
    setSelectedRequest(request);
    setDenyReason('');
    setShowDenyDialog(true);
  };

  // Deny device request
  const handleDeny = async () => {
    if (!selectedRequest) return;
    try {
      await axios.post(`/admin/device-requests/${selectedRequest.id}/deny`, {
        reason: denyReason || 'Request denied by administrator'
      }, {
        withCredentials: true
      });
      toast.success('Device request denied');
      setShowDenyDialog(false);
      setSelectedRequest(null);
      fetchPendingRequests();
      fetchAllRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny request');
    }
  };

  // Toggle device approval mode for user
  const handleToggleDeviceApproval = async (userId, currentValue) => {
    try {
      await axios.post(`/admin/users/${userId}/toggle-device-approval`, {
        enabled: !currentValue
      }, {
        withCredentials: true
      });
      toast.success(`Device approval ${!currentValue ? 'enabled' : 'disabled'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to toggle device approval mode');
    }
  };

  // Delete single history item
  const handleDeleteHistoryItem = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    
    try {
      await axios.delete(`/admin/device-requests/${requestId}`, {
        withCredentials: true
      });
      toast.success('Record deleted');
      fetchAllRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete record');
    }
  };

  // Clear all history
  const handleClearAllHistory = async () => {
    if (!window.confirm('Are you sure you want to clear ALL device request history? This action cannot be undone.')) return;
    
    try {
      const response = await axios.delete('/admin/device-requests?status=all', {
        withCredentials: true
      });
      toast.success(`Cleared ${response.data.deleted_count} record(s)`);
      fetchAllRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear history');
    }
  };

  // View user's approved devices
  const handleViewUserDevices = async (user) => {
    setSelectedUser(user);
    try {
      // We need to use a different approach since my-devices is for current user
      // For admin, we'll show devices from approved_devices collection via the requests
      const approvedForUser = allRequests.filter(
        r => r.user_id === user.id && r.status === 'approved'
      );
      setUserDevices(approvedForUser);
      setShowUserDevicesDialog(true);
    } catch (error) {
      toast.error('Failed to fetch user devices');
    }
  };

  // Filter users by email search
  const filteredUsers = users.filter(user => 
    !searchEmail || 
    (user.email && user.email.toLowerCase().includes(searchEmail.toLowerCase())) ||
    (user.name && user.name.toLowerCase().includes(searchEmail.toLowerCase()))
  );

  // Get device icon based on user agent
  const getDeviceIcon = (userAgent) => {
    if (!userAgent) return <Monitor className="h-5 w-5" />;
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <Smartphone className="h-5 w-5" />;
    }
    return <Monitor className="h-5 w-5" />;
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300"><Check className="h-3 w-3 mr-1" /> Approved</Badge>;
      case 'denied':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300"><X className="h-3 w-3 mr-1" /> Denied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="device-approval-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-indigo-600" />
            Device Approval Management
          </h1>
          <p className="text-gray-500 mt-1">Manage device access requests and user device settings</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchPendingRequests(); fetchAllRequests(); fetchUsers(); }}
          data-testid="refresh-btn"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Pending Requests</p>
                <p className="text-3xl font-bold text-yellow-600">{pendingRequests.length}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Approved Devices</p>
                <p className="text-3xl font-bold text-green-600">
                  {allRequests.filter(r => r.status === 'approved').length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <ShieldCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Denied Requests</p>
                <p className="text-3xl font-bold text-red-600">
                  {allRequests.filter(r => r.status === 'denied').length}
                </p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <ShieldX className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Users with Approval Mode</p>
                <p className="text-3xl font-bold text-indigo-600">
                  {users.filter(u => u.device_approval_mode).length}
                </p>
              </div>
              <div className="p-3 bg-indigo-100 rounded-full">
                <Shield className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
          <TabsTrigger value="pending" className="relative" data-testid="pending-tab">
            Pending
            {pendingRequests.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                {pendingRequests.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">History</TabsTrigger>
          <TabsTrigger value="users" data-testid="users-tab">User Settings</TabsTrigger>
        </TabsList>

        {/* Pending Requests Tab */}
        <TabsContent value="pending" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                Pending Device Approval Requests
              </CardTitle>
              <CardDescription>
                Review and approve or deny new device login requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pendingRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ShieldCheck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No pending device requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingRequests.map((request) => (
                    <div 
                      key={request.id} 
                      className="border rounded-lg p-4 bg-yellow-50 border-yellow-200"
                      data-testid={`pending-request-${request.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="p-3 bg-white rounded-lg shadow-sm">
                            {getDeviceIcon(request.user_agent)}
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <p className="font-semibold text-gray-900">{request.device_name}</p>
                              {getStatusBadge(request.status)}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <User className="h-4 w-4" />
                              <span>{request.user_email || 'Unknown User'}</span>
                              {request.user_name && <span className="text-gray-400">({request.user_name})</span>}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Globe className="h-4 w-4" />
                              <span>IP: {request.ip_address || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Calendar className="h-4 w-4" />
                              <span>Requested: {formatDate(request.created_at)}</span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="border-green-500 text-green-600 hover:bg-green-50"
                            onClick={() => handleApprove(request.id)}
                            data-testid={`approve-btn-${request.id}`}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="border-red-500 text-red-600 hover:bg-red-50"
                            onClick={() => handleDenyClick(request)}
                            data-testid={`deny-btn-${request.id}`}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Deny
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Device Request History</CardTitle>
                <CardDescription>
                  View all past device approval requests
                </CardDescription>
              </div>
              {allRequests.filter(r => r.status !== 'pending').length > 0 && (
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleClearAllHistory}
                  data-testid="clear-all-history-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Clear All History
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {allRequests.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Monitor className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No device requests yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 font-medium text-gray-500">Device</th>
                        <th className="pb-3 font-medium text-gray-500">User</th>
                        <th className="pb-3 font-medium text-gray-500">IP Address</th>
                        <th className="pb-3 font-medium text-gray-500">Requested</th>
                        <th className="pb-3 font-medium text-gray-500">Status</th>
                        <th className="pb-3 font-medium text-gray-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {allRequests.map((request) => (
                        <tr key={request.id} className="hover:bg-gray-50" data-testid={`history-row-${request.id}`}>
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              {getDeviceIcon(request.user_agent)}
                              <span className="text-sm">{request.device_name}</span>
                            </div>
                          </td>
                          <td className="py-3">
                            <span className="text-sm">{request.user_email || 'Unknown'}</span>
                          </td>
                          <td className="py-3">
                            <span className="text-sm text-gray-500">{request.ip_address || 'N/A'}</span>
                          </td>
                          <td className="py-3">
                            <span className="text-sm text-gray-500">{formatDate(request.created_at)}</span>
                          </td>
                          <td className="py-3">
                            {getStatusBadge(request.status)}
                            {request.denial_reason && (
                              <p className="text-xs text-red-500 mt-1">{request.denial_reason}</p>
                            )}
                          </td>
                          <td className="py-3">
                            {request.status !== 'pending' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteHistoryItem(request.id)}
                                data-testid={`delete-history-${request.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* User Settings Tab */}
        <TabsContent value="users" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>User Device Approval Settings</CardTitle>
              <CardDescription>
                Enable or disable device approval mode for individual users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by email or name..."
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    className="pl-10"
                    data-testid="user-search-input"
                  />
                </div>
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-3 font-medium text-gray-500">User</th>
                      <th className="pb-3 font-medium text-gray-500">Role</th>
                      <th className="pb-3 font-medium text-gray-500">Device Approval</th>
                      <th className="pb-3 font-medium text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50" data-testid={`user-row-${user.id}`}>
                        <td className="py-3">
                          <div>
                            <p className="font-medium text-gray-900">{user.email || user.phone}</p>
                            {user.name && <p className="text-sm text-gray-500">{user.name}</p>}
                          </div>
                        </td>
                        <td className="py-3">
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {user.role || 'student'}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={user.device_approval_mode || false}
                              onCheckedChange={() => handleToggleDeviceApproval(user.id, user.device_approval_mode)}
                              data-testid={`toggle-approval-${user.id}`}
                            />
                            <Label className="text-sm text-gray-500">
                              {user.device_approval_mode ? 'Enabled' : 'Disabled'}
                            </Label>
                          </div>
                        </td>
                        <td className="py-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewUserDevices(user)}
                            data-testid={`view-devices-${user.id}`}
                          >
                            <Monitor className="h-4 w-4 mr-1" />
                            View Devices
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredUsers.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No users found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Deny Request Dialog */}
      <Dialog open={showDenyDialog} onOpenChange={setShowDenyDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <ShieldX className="h-5 w-5" />
              Deny Device Request
            </DialogTitle>
            <DialogDescription>
              Provide a reason for denying this device request. The user will be notified via email.
            </DialogDescription>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-500">Device</p>
                <p className="font-medium">{selectedRequest.device_name}</p>
                <p className="text-sm text-gray-500 mt-2">User</p>
                <p className="font-medium">{selectedRequest.user_email}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="deny-reason">Reason for Denial</Label>
                <Textarea
                  id="deny-reason"
                  placeholder="Enter reason (optional)..."
                  value={denyReason}
                  onChange={(e) => setDenyReason(e.target.value)}
                  rows={3}
                  data-testid="deny-reason-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDenyDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeny}
              data-testid="confirm-deny-btn"
            >
              <X className="h-4 w-4 mr-1" />
              Deny Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Devices Dialog */}
      <Dialog open={showUserDevicesDialog} onOpenChange={setShowUserDevicesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Monitor className="h-5 w-5 text-indigo-600" />
              Approved Devices
            </DialogTitle>
            <DialogDescription>
              {selectedUser && `Devices approved for ${selectedUser.email || selectedUser.phone}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {userDevices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Monitor className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No approved devices found</p>
              </div>
            ) : (
              userDevices.map((device, index) => (
                <div key={index} className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <div className="flex items-center gap-3">
                    {getDeviceIcon(device.user_agent)}
                    <div>
                      <p className="font-medium">{device.device_name}</p>
                      <p className="text-sm text-gray-500">Approved: {formatDate(device.approved_at)}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDevicesDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeviceApproval;
